import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { courses, courseCategories } from "../data/mock";
import { Search } from "lucide-react";
import { DoodleIcon, directionIcons } from "../components/DoodleIcon";
import { CourseCard } from "../components/CourseCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import SEO from "../components/SEO";

export const AllCoursesPage = () => {
  const [params, setParams] = useSearchParams();
  const searchQuery = params.get("q") || "";
  const levels = ["С нуля", "Продвинутый уровень"];
  const requestedCategory = params.get("category");
  const activeLevel = levels.includes(params.get("level")) ? params.get("level") : levels.includes(requestedCategory) ? requestedCategory : "";
  const selectedCategory = courseCategories.includes(requestedCategory) && !levels.includes(requestedCategory) ? requestedCategory : "Все";
  const setLevel = value => setParams(prev => { value ? prev.set("level", value) : prev.delete("level"); if (levels.includes(prev.get("category"))) prev.delete("category"); return prev; }, {replace:true});
  const setSearchQuery = value => setParams(prev => { value ? prev.set("q", value) : prev.delete("q"); return prev; }, {replace:true});
  const setSelectedCategory = value => setParams(prev => { value === "Все" ? prev.delete("category") : prev.set("category", value); return prev; }, {replace:true});

  const allCategories = ["Все", ...courseCategories.filter(c => !levels.includes(c))];
  const categoryLabel = c => ({"Веб разработка":"Веб-разработка","Веб Дизайн":"Веб-дизайн","CMS и Конструкторы":"CMS и конструкторы","Искусственный интеллект":"Нейросети"}[c] || c);

  // Group courses by category for "Все" view
  const groupedCourses = useMemo(() => {
    if (selectedCategory !== "Все" || searchQuery || activeLevel) return null;
    
    const groups = {};
    courseCategories.forEach(cat => { groups[cat] = []; });
    
    courses.forEach(course => {
      if (course.hidden) return;
      if (groups[course.category]) {
        groups[course.category].push(course);
      }
    });
    
    // Return only non-empty groups
    return courseCategories
      .filter(cat => groups[cat] && groups[cat].length > 0)
      .map(cat => ({ category: cat, courses: groups[cat] }));
  }, [selectedCategory, searchQuery, activeLevel]);

  const filteredCourses = useMemo(() => {
    const filtered = courses.filter(course => {
      if (course.hidden) return false;
      const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           course.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === "Все" || 
                             course.category === selectedCategory ||
                             (course.additionalCategories && course.additionalCategories.includes(selectedCategory));
      return matchesSearch && matchesCategory && (!activeLevel || course.additionalCategories?.includes(activeLevel) || course.level === activeLevel);
    });
    
    // Custom sorting for specific categories
    if (selectedCategory === "Веб разработка") {
      // HTML Pro (html-css-pro) goes to end
      return filtered.sort((a, b) => {
        if (a.slug === "html-css-pro") return 1;
        if (b.slug === "html-css-pro") return -1;
        return 0;
      });
    }
    
    if (selectedCategory === "Веб Дизайн") {
      // UI веб дизайн (figma) and Веб дизайн Pro (figma-pro-self, figma-pro-support) first
      // Теория дизайна (design-theory) and Figma анимации (figma-animations) last
      const priority = {
        "figma": 1,
        "figma-pro-self": 2,
        "figma-pro-support": 3,
        "design-theory": 98,
        "figma-animations": 99
      };
      return filtered.sort((a, b) => {
        const orderA = priority[a.slug] || 50;
        const orderB = priority[b.slug] || 50;
        return orderA - orderB;
      });
    }
    
    if (selectedCategory === "Продвинутый уровень") {
      // Веб дизайн про first, then React, then JavaScript
      const priority = {
        "figma-pro-self": 1,
        "figma-pro-support": 2,
        "react": 3,
        "javascript": 4
      };
      return filtered.sort((a, b) => {
        const orderA = priority[a.slug] || 50;
        const orderB = priority[b.slug] || 50;
        return orderA - orderB;
      });
    }
    
    return filtered;
  }, [searchQuery, selectedCategory, activeLevel]);

  return (
    <div className="min-h-screen bg-white" data-testid="all-courses-page">
      <SEO 
        title="Каталог курсов"
        description="Выберите курс из нашего каталога: дизайн, программирование, нейросети. Figma, Photoshop, JavaScript, React, After Effects и многое другое. Пожизненный доступ."
        url="/courses"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="catalog-heading bg-[#F1EDE4] pt-28 pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-7">
            <span className="text-teal-400 font-semibold text-sm tracking-wider uppercase mb-4 block">
              Каталог курсов
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Все{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
                курсы
              </span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Выберите направление и начните обучение уже сегодня.{" "}
              {courses.filter(c => !c.hidden).length} курсов по программированию, дизайну и видеомонтажу.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                aria-label="Поиск курсов"
                type="search"
                placeholder="Поиск курсов..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-gray-400 rounded-xl focus:border-teal-400 focus:ring-teal-400/20"
                data-testid="course-search-input"
              />
            </div>
          </div>

          {/* Category Filters */}
          <label className="mobile-category-select">Направление<select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>{allCategories.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}</select></label>
          <div className="desktop-category-filters flex flex-wrap justify-center gap-3">
            {allCategories.map((category) => {
              const isPack = category === "Пакеты курсов";
              const isActive = selectedCategory === category;
              
              return (
                <button
                  aria-pressed={isActive}
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 ${
                    isActive
                      ? isPack 
                        ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-orange-500/30"
                        : "bg-teal-500 text-white shadow-lg shadow-teal-500/30"
                      : isPack
                        ? "bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30"
                        : "bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white"
                  }`}
                >
                  <DoodleIcon name={directionIcons[category] || "book"} />{categoryLabel(category)}
                </button>
              );
            })}
          </div>
          <div className="course-level-row" role="group" aria-label="Уровень подготовки"><span>Уровень</span>{[["", "Любой"], ["С нуля", "С нуля"], ["Продвинутый уровень", "Продвинутый"]].map(([value,label])=><button className="course-level-button" key={value} aria-pressed={activeLevel===value} onClick={()=>setLevel(value)}>{label}</button>)}</div>
        </div>
      </section>

      {/* Courses Grid Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {groupedCourses ? (
            /* Grouped by category view */
            <div className="space-y-16" data-testid="courses-grouped">
              {groupedCourses.map(({ category, courses: catCourses }) => (
                <div key={category}>
                  <div className="flex items-center gap-3 mb-6 flex-wrap">
                    <h2 className="text-2xl font-bold text-gray-900">{category}</h2>
                    <span className="text-sm text-gray-400 bg-gray-200 px-3 py-1 course-category-chip">{catCourses.length}</span>
                    <button 
                      onClick={() => setSelectedCategory(category)}
                      className="ml-auto whitespace-nowrap text-sm text-teal-600 hover:text-teal-700 font-medium transition-colors"
                    >
                      Смотреть все &rarr;
                    </button>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {catCourses.map((course, index) => (
                      <CourseCard key={course.id} course={course} index={index} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Filtered single category view */
            <>
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    {selectedCategory === "Все" ? "Все курсы" : selectedCategory}
                  </h2>
                  <p className="text-gray-500 mt-2">
                    Найдено курсов: {filteredCourses.length}
                  </p>
                </div>
              </div>

              {filteredCourses.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="courses-grid">
                  {filteredCourses.map((course, index) => (
                    <CourseCard key={course.id} course={course} index={index} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16">
                  <div className="text-gray-400 text-6xl mb-4">
                    <Search className="w-16 h-16 mx-auto text-gray-300" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">Курсы не найдены</h3>
                  <p className="text-gray-500">
                    Попробуйте изменить параметры поиска или выберите другую категорию
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AllCoursesPage;
